import { NavLink, Navigate, Outlet, useParams } from 'react-router-dom';
import { BookOpen, Bookmark, CalendarDays, Home, User } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useProgramBranding } from './program-branding';
import { cx } from '../components/ui';

/**
 * Envoltorio de la app del paciente.
 *
 * Mobile-first sin concesiones: casi todo el uso es en el teléfono, muchas veces
 * en una sala de espera. La navegación va abajo, al alcance del pulgar.
 *
 * En escritorio esa misma navegación pasa a una columna lateral. No es un
 * capricho de diseño: una barra de pestañas pegada al borde inferior de un
 * monitor de 27 pulgadas queda lejísimos del contenido, y el paciente que entra
 * desde la computadora —el que revisa su tratamiento con calma, o un familiar
 * que lo acompaña— se encuentra con una columna de teléfono en el medio de la
 * pantalla. El corte va en `lg`: de ahí para abajo, la app no cambia en nada.
 */
export function PatientLayout() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const branding = useProgramBranding();

  if (!user) return <Navigate to={`/${slug}`} replace />;
  if (user.mustChangePassword) return <Navigate to={`/${slug}/cambiar-clave`} replace />;
  if (user.role !== 'PATIENT' && user.role !== 'CAREGIVER') {
    return <Navigate to={`/${slug}/console`} replace />;
  }

  const base = `/${slug}/app`;

  const tabs = [
    { to: base, icon: Home, label: 'Inicio', end: true },
    { to: `${base}/contenidos`, icon: BookOpen, label: 'Contenidos', end: false },
    { to: `${base}/calendario`, icon: CalendarDays, label: 'Calendario', end: false },
    { to: `${base}/guardados`, icon: Bookmark, label: 'Guardados', end: false },
    { to: `${base}/perfil`, icon: User, label: 'Perfil', end: false },
  ];

  return (
    <div className="min-h-screen bg-surface lg:flex">
      {/* Columna lateral: solo escritorio. El ancho va en línea porque en este
          proyecto las utilidades de ancho fijo no siempre se generan. */}
      <aside
        aria-label="Navegación principal"
        className="hidden lg:flex lg:flex-col lg:sticky lg:top-0 lg:h-screen bg-surface-raised border-r border-border"
        style={{ width: '16rem', flexShrink: 0 }}
      >
        <div className="px-5 py-6 border-b border-border">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.programName} className="h-9 object-contain" />
          ) : (
            <p className="font-semibold tracking-tight leading-tight">{branding.programName}</p>
          )}
        </div>

        <ul className="p-3 space-y-1 grow">
          {tabs.map((tab) => (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-[15px] font-medium transition-colors',
                    isActive
                      ? 'bg-primary-soft text-primary'
                      : 'text-fg-muted hover:bg-surface hover:text-fg',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <tab.icon className="size-5 shrink-0" strokeWidth={isActive ? 2.4 : 1.9} aria-hidden />
                    {tab.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <p className="px-5 py-4 text-xs text-fg-subtle border-t border-border">
          {branding.programName}
        </p>
      </aside>

      {/* min-w-0 para que un gráfico o una tabla anchos no estiren la columna. */}
      <div className="grow min-w-0 flex flex-col">
        <main className="grow pb-24 lg:pb-12">
          <Outlet />
        </main>
      </div>

      <nav
        aria-label="Navegación principal"
        className="lg:hidden fixed bottom-0 inset-x-0 bg-surface-raised border-t border-border safe-bottom z-40"
      >
        <ul className="flex max-w-lg mx-auto">
          {tabs.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  cx(
                    'flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors',
                    isActive ? 'text-primary' : 'text-fg-subtle hover:text-fg-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <tab.icon className="size-5" strokeWidth={isActive ? 2.4 : 1.9} aria-hidden />
                    {tab.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/** Encabezado de las pantallas internas de la app. */
export function PatientHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="bg-surface-raised border-b border-border px-5 pt-5 pb-4 safe-top">
      <div className="max-w-lg lg:max-w-3xl mx-auto flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-fg-muted mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
    </header>
  );
}
